/**
 * Okno exportu statistik tisku: jedno pro PDF report i CSV. Období (stejné
 * dlaždice jako stránka), uživatel pro osobní export, u PDF seskupení a seznam
 * úloh, u CSV co exportovat (souhrn podle jednoho seskupení, nebo všechny
 * úlohy období stránkovaně ze serveru), a „Včetně cen".
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Alert,
  Button,
  FilterChips,
  Input,
  Label,
  Modal,
  Select,
  Toggle,
} from '../../components/ui';
import { api } from '../../lib/api';
import { cn } from '../../lib/utils';
import { explainPrintError, unwrap } from './print-stats-call';
import {
  CSV_JOBS_CAP,
  csvDateTime,
  csvDocument,
  csvFilename,
  csvNumber,
  type CustomerOption,
  formatDay,
  type PeriodId,
  periodRange,
} from './print-stats-logic';
import type {
  PrintFilters,
  PrintGroupBy,
  PrintJobRow,
  PrintJobsResult,
  PrintReportResult,
  PrintStatRow,
  PrintStatsResult,
  ToolFail,
} from './print-stats-types';

export const GROUPS: PrintGroupBy[] = ['user', 'printer', 'paper_size', 'color_mode', 'day'];
const JOBS_PAGE = 200;

export type ExportKind = 'pdf' | 'csv';
type CsvWhat = PrintGroupBy | 'jobs';

export function saveBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function saveBase64(filename: string, b64: string, contentType: string) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  saveBlob(filename || 'tisky.pdf', new Blob([bytes], { type: contentType || 'application/pdf' }));
}

export function periodOptions(t: TFunction): { value: PeriodId; label: string }[] {
  return [
    { value: 'today', label: t('printserver.stats.periodToday') },
    { value: 'yesterday', label: t('printserver.stats.periodYesterday') },
    { value: 'week', label: t('printserver.stats.periodWeek') },
    { value: 'lastWeek', label: t('printserver.stats.periodLastWeek') },
    { value: 'this', label: t('printserver.stats.periodThis') },
    { value: 'last', label: t('printserver.stats.periodLast') },
    { value: '7d', label: t('printserver.stats.period7') },
    { value: '30d', label: t('printserver.stats.period30') },
    { value: 'custom', label: t('printserver.stats.periodCustom') },
  ];
}

function periodTileCaption(
  t: TFunction,
  id: PeriodId,
  now: Date,
  custom: { from: string; to: string },
): string {
  if (id === 'custom' && (!custom.from || !custom.to)) {
    return t('printserver.stats.periodCustomHint');
  }
  const range = periodRange(id, now, custom);
  if (range.from === range.to) return formatDay(range.from);
  return t('printserver.stats.pdfSpan', {
    from: formatDay(range.from),
    to: formatDay(range.to),
  });
}

/** Dlaždice období. Stejné na stránce statistik, v aplikaci Tisky i v okně reportu. */
export function PeriodTiles({
  label,
  value,
  onChange,
  customFrom,
  customTo,
}: {
  label: string;
  value: PeriodId;
  onChange: (next: PeriodId) => void;
  customFrom: string;
  customTo: string;
}) {
  const { t } = useTranslation();
  const now = new Date();
  return (
    <div role='radiogroup' aria-label={label} className='grid grid-cols-2 gap-2 sm:grid-cols-3'>
      {periodOptions(t).map((option) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type='button'
            role='radio'
            aria-checked={on}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-2xl acm-glass acm-glass-edge px-3 py-3 text-left transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-plex/60',
              on ? 'ring-2 ring-plex' : 'hover:ring-1 hover:ring-gray-500/50',
            )}
          >
            <span className={cn('block text-sm font-semibold', on ? 'text-plex' : 'text-gray-100')}>
              {option.label}
            </span>
            <span className='mt-0.5 block text-xs text-gray-400'>
              {periodTileCaption(t, option.value, now, { from: customFrom, to: customTo })}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Uživatelé období do výběru; vybraný zůstane, i když v období není. */
function userOptions(rows: PrintStatRow[], selected: string | null) {
  const options = rows.filter((row) => row.key).map((row) => ({
    value: row.key,
    label: row.label && row.label !== row.key ? `${row.label} (${row.key})` : row.key,
  }));
  if (selected && !options.some((o) => o.value === selected)) {
    options.unshift({ value: selected, label: selected });
  }
  return options;
}

function MultiChips<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T[];
  onChange: (next: T[]) => void;
}) {
  return (
    <div role='group' aria-label={label} className='flex flex-wrap gap-2'>
      {options.map((option) => {
        const on = value.includes(option.value);
        return (
          <button
            key={option.value}
            type='button'
            aria-pressed={on}
            onClick={() => {
              onChange(
                on ? value.filter((item) => item !== option.value) : [...value, option.value],
              );
            }}
            className={'inline-flex items-center rounded-md border px-3 py-1.5 text-sm transition-colors ' +
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-plex/60 ' +
              (on
                ? 'border-plex/50 bg-plex/18 text-plex'
                : 'border-gray-700 bg-gray-900/60 text-gray-300 hover:border-gray-500 hover:text-gray-100')}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export interface ExportInitial {
  period: PeriodId;
  from: string;
  to: string;
  groupBy: PrintGroupBy;
  user: string | null;
  includeCost: boolean;
}

interface ExportDialogProps {
  kind: ExportKind | null;
  onClose: () => void;
  mode: 'module' | 'app';
  scope: { customer?: string };
  scopeKey: string;
  ready: boolean;
  showPicker: boolean;
  customers: CustomerOption[];
  selected: string;
  onCustomer: (id: string) => void;
  initial: ExportInitial;
  /** Filtr seznamu úloh ze stránky (uživatel / tiskárna), když export není osobní. */
  pageFilters: PrintFilters;
  onCallError: (error: unknown) => void;
}

export function ExportDialog(props: ExportDialogProps) {
  const { kind, onClose, mode, scope, scopeKey, ready, initial, pageFilters } = props;
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'cs';
  const [period, setPeriod] = useState<PeriodId>(initial.period);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [user, setUser] = useState<string | null>(initial.user);
  const [groups, setGroups] = useState<PrintGroupBy[]>([initial.groupBy]);
  const [csvWhat, setCsvWhat] = useState<CsvWhat>(initial.user ? 'jobs' : initial.groupBy);
  const [includeJobs, setIncludeJobs] = useState(!!initial.user);
  const [includeCost, setIncludeCost] = useState(initial.includeCost);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  // Každé otevření začíná hodnotami ze stránky.
  useEffect(() => {
    if (!kind) return;
    setPeriod(initial.period);
    setFrom(initial.from);
    setTo(initial.to);
    setUser(initial.user);
    setGroups([initial.groupBy]);
    setCsvWhat(initial.user ? 'jobs' : initial.groupBy);
    setIncludeJobs(!!initial.user);
    setIncludeCost(initial.includeCost);
    setError(ready ? null : t('printserver.stats.errCustomer'));
    setNotice(null);
    setProgress(null);
  }, [kind]);

  const range = useMemo(() => periodRange(period, new Date(), { from, to }), [period, from, to]);
  const filters: PrintFilters = user ? { user } : pageFilters;

  const usersQ = useQuery({
    queryKey: ['printserver', 'stats', mode, scopeKey, range.from, range.to, 'export-users'],
    enabled: ready && !!kind,
    queryFn: async () =>
      unwrap(
        await api.mcp<PrintStatsResult | ToolFail>('printserver.print_stats', {
          ...scope,
          from: range.from,
          to: range.to,
          group_by: 'user',
          limit: 500,
        }, { silent: true }),
        t('printserver.stats.loadFailed'),
      ),
  });

  const pickUser = (value: string | null) => {
    setUser(value);
    setIncludeJobs(!!value);
    if (value) setCsvWhat('jobs');
  };

  const runPdf = async (): Promise<null> => {
    const result = unwrap(
      await api.mcp<PrintReportResult | ToolFail>('printserver.print_report', {
        ...scope,
        from: range.from,
        to: range.to,
        group_by: groups,
        ...(user ? { user } : { filters }),
        include_jobs: includeJobs,
        include_cost: includeCost,
        lang: language.startsWith('en') ? 'en' : 'cs',
      }, { silent: true }),
      t('printserver.stats.pdfFailed'),
    );
    saveBase64(result.filename, result.base64, result.content_type);
    return null;
  };

  const runCsvSummary = async (groupBy: PrintGroupBy) => {
    const result = unwrap(
      await api.mcp<PrintStatsResult | ToolFail>('printserver.print_stats', {
        ...scope,
        from: range.from,
        to: range.to,
        group_by: groupBy,
        limit: 500,
        filters,
      }, { silent: true }),
      t('printserver.stats.csvFailed'),
    );
    const total = result.totals.total_pages;
    const headers = [
      t('printserver.stats.colName'),
      t('printserver.stats.csvKey'),
      t('printserver.stats.colPages'),
      t('printserver.stats.csvSharePct'),
      t('printserver.stats.colJobs'),
      t('printserver.stats.colColor'),
      t('printserver.stats.colMono'),
      'A4',
      'A3',
      ...(includeCost
        ? [t('printserver.stats.csvCost', { currency: result.totals.currency ?? 'CZK' })]
        : []),
    ];
    const rows = result.rows.map((row) => [
      csvName(t, groupBy, row),
      row.key,
      csvNumber(row.total_pages, language),
      csvNumber(total > 0 ? (row.total_pages / total) * 100 : 0, language, 1),
      csvNumber(row.jobs, language),
      csvNumber(row.color_pages, language),
      csvNumber(row.mono_pages, language),
      csvNumber(row.a4_pages, language),
      csvNumber(row.a3_pages, language),
      ...(includeCost ? [csvNumber(row.cost ?? 0, language, 2)] : []),
    ]);
    return {
      csv: csvDocument(headers, rows),
      filename: csvFilename(
        result.customer ?? scope.customer ?? '',
        user,
        range.from,
        range.to,
        false,
      ),
    };
  };

  const runCsvJobs = async () => {
    const all: PrintJobRow[] = [];
    let total = 0;
    let customer = scope.customer ?? '';
    for (let offset = 0; offset < CSV_JOBS_CAP; offset += JOBS_PAGE) {
      const page = unwrap(
        await api.mcp<PrintJobsResult | ToolFail>('printserver.print_jobs', {
          ...scope,
          from: range.from,
          to: range.to,
          filters,
          sort: 'printed_at',
          order: 'asc',
          offset,
          limit: JOBS_PAGE,
        }, { silent: true }),
        t('printserver.stats.csvFailed'),
      );
      total = page.total;
      customer = page.customer ?? customer;
      all.push(...page.rows);
      setProgress({ done: Math.min(all.length, total), total: Math.min(total, CSV_JOBS_CAP) });
      if (!page.rows.length || all.length >= total) break;
    }
    const rows = all.slice(0, CSV_JOBS_CAP);
    const color = (mode: string | null) =>
      mode === 'color'
        ? t('printserver.stats.colorColor')
        : mode === 'mono'
        ? t('printserver.stats.colorMono')
        : t('printserver.stats.colorUnknown');
    const headers = [
      t('printserver.stats.colWhen'),
      t('printserver.stats.colUser'),
      t('printserver.stats.csvLogin'),
      t('printserver.stats.colPrinter'),
      t('printserver.stats.colDocument'),
      t('printserver.stats.csvPagesPerCopy'),
      t('printserver.stats.colCopies'),
      t('printserver.stats.colPages'),
      t('printserver.stats.colPaper'),
      t('printserver.stats.colColor'),
      t('printserver.stats.colDuplex'),
      t('printserver.stats.colServer'),
      ...(includeCost ? [t('printserver.stats.csvCost', { currency: 'CZK' })] : []),
    ];
    const lines = rows.map((row) => [
      csvDateTime(row.printed_at),
      row.label || row.user_name || '',
      row.user_name ?? '',
      row.printer,
      row.document ?? '',
      csvNumber(row.pages, language),
      csvNumber(row.copies, language),
      csvNumber(row.total_pages, language),
      row.paper_size ?? '',
      color(row.color_mode),
      row.duplex == null ? '' : row.duplex ? t('printserver.stats.yes') : t('printserver.stats.no'),
      row.server,
      ...(includeCost ? [csvNumber(row.cost ?? 0, language, 2)] : []),
    ]);
    return {
      csv: csvDocument(headers, lines),
      filename: csvFilename(customer, user, range.from, range.to, true),
      truncated: total > rows.length ? { shown: rows.length, total } : null,
    };
  };

  const run = useMutation({
    mutationFn: async (): Promise<{ shown: number; total: number } | null> => {
      setError(null);
      setNotice(null);
      setProgress(null);
      if (kind === 'pdf') return runPdf();
      if (csvWhat === 'jobs') {
        const jobs = await runCsvJobs();
        saveBlob(jobs.filename, new Blob([jobs.csv], { type: 'text/csv;charset=utf-8' }));
        return jobs.truncated;
      }
      const summary = await runCsvSummary(csvWhat);
      saveBlob(summary.filename, new Blob([summary.csv], { type: 'text/csv;charset=utf-8' }));
      return null;
    },
    onSuccess: (truncated) => {
      setProgress(null);
      if (truncated) {
        setNotice(t('printserver.stats.csvTruncated', {
          shown: truncated.shown.toLocaleString(language),
          total: truncated.total.toLocaleString(language),
        }));
        return;
      }
      onClose();
    },
    onError: (err: Error) => {
      setProgress(null);
      props.onCallError(err);
      setError(
        explainPrintError(
          t,
          err,
          t(kind === 'pdf' ? 'printserver.stats.pdfFailed' : 'printserver.stats.csvFailed'),
        ),
      );
    },
  });

  const groupLabel = (id: PrintGroupBy) => t(`printserver.stats.group.${id}`);
  const blocked = !ready || (kind === 'pdf' && !user && groups.length === 0);

  return (
    <Modal
      open={!!kind}
      onClose={onClose}
      title={kind === 'csv' ? t('printserver.stats.csvTitle') : t('printserver.stats.pdfTitle')}
      size='lg'
      footer={
        <div className='flex justify-end gap-2'>
          <Button variant='secondary' size='md' onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant='primary'
            size='md'
            loading={run.isPending}
            disabled={blocked}
            onClick={() => run.mutate()}
          >
            {kind === 'csv'
              ? t('printserver.stats.csvGenerate')
              : t('printserver.stats.pdfGenerate')}
          </Button>
        </div>
      }
    >
      <div className='space-y-4'>
        {props.showPicker && (
          <div>
            <Label>{t('printserver.stats.customer')}</Label>
            <Select
              size='md'
              value={props.selected}
              onChange={(event) => props.onCustomer(event.target.value)}
              options={props.customers.map((item) => ({ value: item.id, label: item.label }))}
              placeholder={t('printserver.stats.pickCustomer')}
            />
          </div>
        )}
        <div>
          <Label>{t('printserver.stats.period')}</Label>
          <PeriodTiles
            label={t('printserver.stats.period')}
            value={period}
            onChange={setPeriod}
            customFrom={from}
            customTo={to}
          />
          {period === 'custom' && (
            <div className='mt-3 grid gap-3 sm:grid-cols-2'>
              <div>
                <Label>{t('printserver.stats.from')}</Label>
                <Input
                  size='md'
                  type='date'
                  value={from}
                  onChange={(event) => setFrom(event.target.value)}
                />
              </div>
              <div>
                <Label>{t('printserver.stats.to')}</Label>
                <Input
                  size='md'
                  type='date'
                  value={to}
                  onChange={(event) => setTo(event.target.value)}
                />
              </div>
            </div>
          )}
        </div>
        <div>
          <Label>{t('printserver.stats.pdfUser')}</Label>
          <div className='flex flex-wrap items-center gap-2'>
            <div className='w-64'>
              <Select
                size='md'
                value={user ?? ''}
                onChange={(event) => pickUser(event.target.value || null)}
                options={[
                  { value: '', label: t('printserver.stats.pdfWholeOrg') },
                  ...userOptions(usersQ.data?.rows ?? [], user),
                ]}
              />
            </div>
            {user && (
              <Button size='md' variant='ghost' onClick={() => pickUser(null)}>
                {t('printserver.stats.pdfClearUser')}
              </Button>
            )}
          </div>
          {user && (
            <p className='mt-2 text-xs text-gray-400'>
              {kind === 'csv'
                ? t('printserver.stats.csvPersonalHint')
                : t('printserver.stats.pdfPersonalHint')}
            </p>
          )}
        </div>
        {kind === 'pdf' && !user && (
          <div>
            <Label>{t('printserver.stats.pdfGroups')}</Label>
            <MultiChips
              label={t('printserver.stats.pdfGroups')}
              options={GROUPS.map((id) => ({ value: id, label: groupLabel(id) }))}
              value={groups}
              onChange={setGroups}
            />
          </div>
        )}
        {kind === 'csv' && (
          <div>
            <Label>{t('printserver.stats.csvWhat')}</Label>
            <FilterChips
              label={t('printserver.stats.csvWhat')}
              value={csvWhat}
              onChange={setCsvWhat}
              options={[
                ...GROUPS.map((id) => ({ value: id as CsvWhat, label: groupLabel(id) })),
                { value: 'jobs' as CsvWhat, label: t('printserver.stats.csvJobs') },
              ]}
            />
          </div>
        )}
        {kind === 'pdf' && (
          <Toggle
            label={t('printserver.stats.pdfIncludeJobs')}
            value={includeJobs}
            onSave={setIncludeJobs}
          />
        )}
        <Toggle
          label={t('printserver.stats.pdfIncludeCost')}
          value={includeCost}
          onSave={setIncludeCost}
        />
        {run.isPending && (
          <p className='text-sm text-gray-400'>
            {progress
              ? t('printserver.stats.csvProgress', {
                done: progress.done.toLocaleString(language),
                total: progress.total.toLocaleString(language),
              })
              : kind === 'csv'
              ? t('printserver.stats.csvWorking')
              : t('printserver.stats.pdfWorking')}
          </p>
        )}
        {notice && <Alert variant='warning'>{notice}</Alert>}
        {error && <Alert variant='destructive'>{error}</Alert>}
      </div>
    </Modal>
  );
}

function csvName(t: TFunction, groupBy: PrintGroupBy, row: PrintStatRow): string {
  if (groupBy === 'color_mode') {
    if (row.key === 'color') return t('printserver.stats.colorColor');
    if (row.key === 'mono') return t('printserver.stats.colorMono');
    return t('printserver.stats.colorUnknown');
  }
  if (!row.key) return t('printserver.stats.unknown');
  return row.label || row.key;
}
